/**
 * Single source of truth for WHO sees the onboarding (tour + section guides).
 *
 * - ``"demo"`` — public demo's ephemeral visitors: always-on guidance.
 * - ``"user"`` — a real signed-in user: first-run guidance (shown once, then
 *   dismissible per user).
 * - ``null`` — no session / nobody to onboard.
 *
 * Decoupling the audience from the components means enabling/changing who gets
 * onboarded is a one-place edit. ``onboardingKey`` namespaces the "seen"/
 * "dismissed" persistence so demo (per browser) and each real user (per
 * user_id) are tracked independently.
 */
import { readCachedSession } from "@/features/auth/session";

export type OnboardingMode = "demo" | "user";

export function resolveOnboardingMode(): OnboardingMode | null {
  const session = readCachedSession();
  if (!session) return null;
  return session.is_demo ? "demo" : "user";
}

/** Persistence key for the active audience (``"demo"`` or ``"user:<id>"``). */
export function onboardingKey(): string {
  const session = readCachedSession();
  if (!session) return "anon";
  return session.is_demo ? "demo" : `user:${session.user_id}`;
}

/** Hook form for render-time gating. */
export function useOnboardingMode(): OnboardingMode | null {
  return resolveOnboardingMode();
}
